import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownTrayIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import FormButton from '@app/component/form/button/FormButton';
import ToolbarButton from '@ui/button/ToolbarButton';
import Message from '@app/component/message/Message';
import CheckboxMenu from '@app/component/menu/CheckboxMenu';
import DashboardPlaceholder from '@app/component/placeholder/DashboardPlaceholder';
import { RevenueScope, SegmentationView, VatBasis } from '@app/model/bi/Bi';
import { useSegmentationReport, useSegmentGroups } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import SegmentEvolutionCard from '../fragment/SegmentEvolutionCard';
import SegmentationTableCard, { ColumnSet } from '../fragment/SegmentationTableCard';
import SegmentGroupsDrawer, { SegmentGroupsDrawerHandle } from '../fragment/SegmentGroupsDrawer';
import { exportFileName, formatDateTime, periodPresets, previousYearHint, propertyCount, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';
import BiSwitchRow from '../fragment/BiSwitchRow';
import useColumnSets from '../useColumnSets';
import useBiView from '../useBiView';
import BiViewBy from '../fragment/BiViewBy';
import CompareButton from '../compare/CompareButton';
import useWideScreen from '../useWideScreen';

// The column sets of the table (grill decision 28), in the order of the Columns menu. Previous year is not in the menu:
// the import holds no stay of the year before (the hint of the menu says so).
const COLUMN_SETS: { value: ColumnSet, label: string }[] = [
	{ value: 'today', label: 'Today' },
	{ value: 'd1', label: 'Situation D-1' }, { value: 'd3', label: 'Situation D-3' }, { value: 'd7', label: 'Situation D-7' },
	{ value: 'd14', label: 'Situation D-14' }, { value: 'd30', label: 'Situation D-30' },
	{ value: 'pu1', label: 'PU 1' }, { value: 'pu3', label: 'PU 3' }, { value: 'pu7', label: 'PU 7' }, { value: 'pu14', label: 'PU 14' }, { value: 'pu30', label: 'PU 30' },
];
const BLOCKS = COLUMN_SETS.map(c => c.value);
const DEFAULT_SETS: ColumnSet[] = ['today', 'd7', 'd30', 'pu7', 'pu30'];

// The Segmentation report (ticket 1 of the BI section): the sold room nights and the revenue of a period of stay
// months by the user's own groups of PMS segments, with the situations D-n and the pickup, and the evolution by stay
// month or week. One period, "Stay months", controls the chart and the table (up to MAX_MONTHS months).
const BiSegmentationPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const groups = useSegmentGroups();
	const hasGroups = (groups.data?.groups.length ?? 0) > 0;
	// The shared View by of the BI reports (the browser keeps the choice); the API names the single items SEGMENTS.
	const { view: shown, setView } = useBiView('SEGMENTATION', hasGroups);
	const view: SegmentationView = shown === 'GROUPS' ? 'GROUPS' : 'SEGMENTS';
	const report = useSegmentationReport(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, view,
		scope.data !== undefined && groups.data !== undefined && address.ready);
	const [sets, toggle] = useColumnSets('SEGMENTATION', BLOCKS, DEFAULT_SETS);
	const width = useBiWidthClass();
	const wide = useWideScreen();
	const drawer = useRef<SegmentGroupsDrawerHandle>(null);
	const toaster = useToaster();
	// true while the Excel file downloads: Export shows "Preparing…" and is disabled.
	const [exporting, setExporting] = useState(false);
	const { first, last } = address;
	const presets = useMemo(() => periodPresets(address.asOfMonth, first, last), [address.asOfMonth, first, last]);

	if (scope.isError || groups.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Segmentation report did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (scope.data === undefined || groups.data === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	const s = scope.data;
	const g = groups.data;
	// The properties of the selection: one property, the properties of a Property group, or all properties.
	const withData = address.properties.some(p => p.reservationsSource !== null);
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	// ROO2-277 D24: the import time is information about the data: it sits in the sublabel of the title (its own line on a
	// phone), and the right of the header holds the actions only (Compare properties, Full width).
	const imported = address.importedAt === null ? null : formatDateTime(address.importedAt);
	const sublabel = `${address.property === null ? propertyCount(address.properties.length) : address.property.area} · Segmentation${hasGroups ? '' : ' · no groups yet'}${wide && imported !== null ? ` · Reservations as of ${imported}` : ''}`;
	// The name of the scope in the file name: the property, the Property group, or null (All properties).
	const scopeName = address.property !== null ? shortName(address.property, s.companyName) : (address.propertyGroup?.title ?? null);
	const openEditor = () => drawer.current?.open();
	const setScope = (value: RevenueScope) => address.set({ revenue: value === 'TOTAL' ? 'total' : 'room' });
	const setVat = (value: VatBasis) => address.set({ vat: value === 'NET' ? 'net' : 'incl' });

	// The Excel file holds the table as the page shows it (ROO2-261): the same view, period, revenue and column sets. Export
	// waits for the table: it is off while the table loads, shows the old period dimmed, or failed.
	const exportReady = report.data !== undefined && !report.isPlaceholderData && !report.isError && !exporting;
	const exportFile = () => {
		setExporting(true);
		BiService.downloadSegmentationExcel({
			companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from, stayTo: address.period.to,
			view, scope: address.revenueScope, vat: address.vat, columns: sets,
		})
			.then(blob => saveFile(blob, exportFileName(scopeName, address.period, s.asOf)))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};

	const tableControls = <>
		<ToolbarButton icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!exportReady} onClick={exportFile}>
			{exporting ? 'Preparing…' : 'Export'}
		</ToolbarButton>
		<CheckboxMenu label={<><TableCellsIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />Columns<span className="font-normal text-gray-500">· {sets.length}</span></>}
			options={COLUMN_SETS} selected={sets} onToggle={toggle} min={1} anchor="bottom end" variant="button"
			hint={previousYearHint(address.asOfMonth, first)} />
	</>;

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Segmentation" fullWidthSwitch>
			{!wide && imported !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{imported}</span></span>}
			<CompareButton search={address.search} properties={s.properties.length} />
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			{!withData && <Message state="info" title="No reservations for this scope" flush>
				{g.canEdit
					? <p>The Segmentation report reads the PMS segments of the imported reservations. Connect a Reservations integration in{' '}
						<Link to={address.companyHotelId === null ? '/secured/settings/integrations' : `/secured/settings/integrations/${address.companyHotelId}#reservations`}
							className="font-medium underline">Settings › Integrations</Link>.</p>
					: <p>The Segmentation report reads the PMS segments of the imported reservations. Ask your company admin to connect a
						Reservations integration.</p>}
			</Message>}
			{withData && !hasGroups && <Message state="info" title={<span>Group your PMS segments</span>} flush>
				{g.canEdit
					? <p>Put segments such as “E1 - OTA net” and “E2 - OTA lordo” into your own groups, for example OTAs. The groups apply to all
						properties of {s.companyName}.{' '}
						<FormButton state="text" isLink size="small" onClick={openEditor}>Create the first group →</FormButton></p>
					: <p>Your company admin can put the PMS segments into groups, for example OTAs. Until then, the report shows the PMS segments.</p>}
			</Message>}
			{withData && <>
				<BiSwitchRow scope={address.revenueScope} vat={address.vat} onScope={setScope} onVat={setVat} period={address.period}
					onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last} sources={sources}>
					<BiViewBy value={shown} onChange={setView} hasGroups={hasGroups} singleLabel="PMS segments" label="Rows of the report"
						onEdit={g.canEdit ? openEditor : null} editLabel="Edit groups" />
				</BiSwitchRow>
				<SegmentEvolutionCard companyHotelId={address.companyHotelId} propertyGroupId={address.propertyGroupId} ready={address.ready} period={address.period} view={view} scope={address.revenueScope} vat={address.vat} asOf={s.asOf} scopeName={scopeName}
					firstStay={address.firstStay} lastStay={address.lastStay} />
				<SegmentationTableCard report={report.data} period={address.period} columnSets={sets} asOf={s.asOf}
					scope={address.revenueScope} vat={address.vat} actions={tableControls}
					state={report.isError ? 'error' : (report.data === undefined ? 'loading' : (report.isPlaceholderData ? 'refreshing' : 'default'))}
					onRetry={() => void report.refetch()} />
			</>}
		</main>
		{g.canEdit && <SegmentGroupsDrawer ref={drawer} data={g} companyName={s.companyName} propertyCount={s.companyProperties} />}
	</>;
};

export default BiSegmentationPage;
